import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { useAboutTranslations } from '../context/aboutTranslations';
import MainLayout from '../components/MainLayout';
import {
  CheckIcon,
  ShieldCheckIcon,
  SparklesIcon,
  // ChevronRightIcon,
  BoltIcon,
  LockClosedIcon,
  ChatBubbleLeftIcon,
  ArrowPathIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  RocketLaunchIcon,
  PaintBrushIcon,
  ChartBarIcon,
  GlobeAltIcon,
  UserGroupIcon,
  ChartPieIcon,
  LightBulbIcon,
  CurrencyEuroIcon,
  UsersIcon,
  FlagIcon,
  EyeIcon,
  HeartIcon,
  // TrustIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import NavBar from '../features/navbar/Navbar';
import Footer from '../features/common/Footer';

// Animated Section Component
const AnimatedCard = ({ children, delay = 0 }) => {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      whileHover={{ y: -5 }}
    >
      {children}
    </motion.div>
  );
};

// Value Item Component
const ValueItem = ({ icon: Icon, title, description, delay }) => (
  <AnimatedCard delay={delay}>
    <div className="bg-white rounded-lg p-8 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-500">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-blue-50 text-blue-600 mb-4">
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
    </div>
  </AnimatedCard>
);

// Animated Section Wrapper
const AnimatedSection = ({ children, delay = 0 }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Animated Grid Items
const AnimatedGridItem = ({ children, delay = 0 }) => {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      whileHover={{ y: -5 }}
    >
      {children}
    </motion.div>
  );
};

const About = () => {
  const { language } = useLanguage();
  const t = useAboutTranslations(language);
  const differentiators = [
    {
      icon: ShieldCheckIcon,
      title: t.differenceGenuineTitle,
      description: t.differenceGenuineDesc,
    },
    {
      icon: BoltIcon,
      title: t.differenceInstantTitle,
      description: t.differenceInstantDesc,
    },
    {
      icon: LockClosedIcon,
      title: t.differenceSecureTitle,
      description: t.differenceSecureDesc,
    },
    {
      icon: ChatBubbleLeftIcon,
      title: t.differenceSupportTitle,
      description: t.differenceSupportDesc,
    },
    {
      icon: ArrowPathIcon,
      title: t.differenceCancelTitle,
      description: t.differenceCancelDesc,
    },
  ];

  const audience = [
    {
      icon: AcademicCapIcon,
      title: t.forStudentsTitle,
      description: t.forStudentsDesc,
    },
    {
      icon: BriefcaseIcon,
      title: t.forProfessionalsTitle,
      description: t.forProfessionalsDesc,
    },
    {
      icon: RocketLaunchIcon,
      title: t.forStartupsTitle,
      description: t.forStartupsDesc,
    },
    {
      icon: PaintBrushIcon,
      title: t.forFreelancersTitle,
      description: t.forFreelancersDesc,
    },
  ];

  const problems = [
    {
      icon: ChartBarIcon,
      title: t.problemHighCostsTitle,
      description: t.problemHighCostsDesc,
    },
    {
      icon: LockClosedIcon,
      title: t.problemTrustTitle,
      description: t.problemTrustDesc,
    },
  ];

  const pricingReasons = [
    {
      icon: GlobeAltIcon,
      title: t.pricesDigitalOpsTitle,
      description: t.pricesDigitalOpsDesc,
    },
    {
      icon: UserGroupIcon,
      title: t.pricesNegotiationsTitle,
      description: t.pricesNegotiationsDesc,
    },
    {
      icon: ChartPieIcon,
      title: t.pricesOptimizationTitle,
      description: t.pricesOptimizationDesc,
    },
  ];

  return (
    <MainLayout>
      <NavBar>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 to-blue-700 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight text-white mb-4 sm:mb-6">
                {t.pageTitle}
              </h1>
              <p className="text-base sm:text-lg text-blue-50 font-light leading-relaxed">
                {t.pageSubtitle}
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
        </section>

        {/* Who We Are */}
        <AnimatedSection>
          <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-3 flex items-center justify-center gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                    <FlagIcon className="w-4 h-4" />
                  </span>
                  {t.whoWeAreTitle} 
                </h2>
              </div>
              {/* <h1>5679</h1> */}
              <div className="bg-slate-100 rounded-xl border border-slate-200 p-6 sm:p-8 shadow-subtle">
                <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
                  {t.whoWeAreDesc}
                </p>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* The Problem */}
        <AnimatedSection>
          <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-blue-50/50">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-3 flex items-center justify-center gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                    <LightBulbIcon className="w-4 h-4" />
                  </span>
                  {t.problemTitle}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {problems.map((item, idx) => (
                  <AnimatedGridItem key={idx} delay={idx * 0.15}>
                    <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 hover:border-blue-200 hover:shadow-subtle transition-all duration-300">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-500 mb-4">
                        <item.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </AnimatedGridItem>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Our Solution */}
        <AnimatedSection>
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-3 flex items-center justify-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                  <BoltIcon className="w-4 h-4" />
                </span>
                {t.solutionTitle}
              </h2>
            </div>
            <div className="bg-blue-50 rounded-xl p-6 sm:p-8 border border-blue-100">
              <p className="text-slate-700 text-base sm:text-lg leading-relaxed mb-6">
                {t.solutionIntro}
              </p>
              <div className="space-y-3">
                {[
                  t.solutionPoint1,
                  t.solutionPoint2,
                  t.solutionPoint3,
                  t.solutionPoint4,
                ].map((text, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 flex-shrink-0 mt-0.5">
                      <CheckIcon className="w-3 h-3 text-white" />
                    </span>
                    <p className="text-slate-700 text-sm">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        </AnimatedSection>

        {/* Why Our Prices Are Lower */}
        <AnimatedSection>
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-blue-50/50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-3 flex items-center justify-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                  <CurrencyEuroIcon className="w-4 h-4" />
                </span>
                {t.pricesTitle}
              </h2>
              <p className="text-slate-600 text-base">
                {t.pricesSubtitle}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {pricingReasons.map((item, idx) => (
                <AnimatedGridItem key={idx} delay={idx * 0.15}>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 hover:border-blue-200 hover:shadow-subtle transition-all duration-300">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-500 mb-4">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </AnimatedGridItem>
              ))}
            </div>
          </div>
        </section>
        </AnimatedSection>

        {/* What Makes Us Different */}
        <AnimatedSection>
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-3 flex items-center justify-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                  <SparklesIcon className="w-4 h-4" />
                </span>
                {t.differenceTitle}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {differentiators.map((item, idx) => (
                <AnimatedGridItem key={idx} delay={idx * 0.12}>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 hover:border-blue-200 hover:shadow-subtle transition-all duration-300">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-500 mb-4">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </AnimatedGridItem>
              ))}
            </div>
          </div>
        </section>
        </AnimatedSection>

        {/* Who We're For */}
        <AnimatedSection>
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-blue-50/50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-3 flex items-center justify-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                  <UsersIcon className="w-4 h-4" />
                </span>
                {t.forTitle}
              </h2>
              <p className="text-slate-600 text-base">
                {t.forSubtitle}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {audience.map((item, idx) => (
                <AnimatedGridItem key={idx} delay={idx * 0.15}>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 hover:border-blue-200 hover:shadow-subtle transition-all duration-300">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-500 mb-4">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </AnimatedGridItem>
              ))}
            </div>
          </div>
        </section>
        </AnimatedSection>

        {/* Mission & Vision */}
        <AnimatedSection>
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
                className="bg-white rounded-xl p-6 sm:p-8 border border-slate-200"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500 text-white">
                    <FlagIcon className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-light text-slate-900">
                    {t.missionTitle}
                  </h3>
                </div>
                <p className="text-slate-700 text-base leading-relaxed">
                  {t.missionDesc}
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.3 }}
                className="bg-blue-500 rounded-xl p-6 sm:p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/20 text-white">
                    <EyeIcon className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-light text-white">
                    {t.visionTitle}
                  </h3>
                </div>
                <p className="text-blue-50 text-base leading-relaxed">
                  {t.visionDesc}
                </p>
              </motion.div>
            </div>
          </div>
        </section>
        </AnimatedSection>

        {/* Security & Transparency */}
        <AnimatedSection>
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-blue-50/50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-3 flex items-center justify-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                  <LockClosedIcon className="w-4 h-4" />
                </span>
                {t.securityTitle}
              </h2>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
              className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 shadow-subtle"
            >
              <div className="space-y-5">
                {[
                  {
                    title: t.securityBankTitle,
                    desc: t.securityBankDesc,
                  },
                  {
                    title: t.securityPrivacyTitle,
                    desc: t.securityPrivacyDesc,
                  },
                  {
                    title: t.securityPartnersTitle,
                    desc: t.securityPartnersDesc,
                  },
                  {
                    title: t.securityReportTitle,
                    desc: t.securityReportDesc,
                  },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: idx * 0.1, ease: "easeOut" }}
                    viewport={{ once: true }}
                    className="flex gap-4 items-start"
                  >
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-500 flex-shrink-0">
                      <ShieldCheckIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
        </AnimatedSection>

        {/* Customer Stories */}
        <AnimatedSection>
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-3 flex items-center justify-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                  <HeartIcon className="w-4 h-4" />
                </span>
                {t.testimonialsTitle}
              </h2>
              <p className="text-slate-600 text-base">
                {t.testimonialsSubtitle}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {t.testimonials.map((item, idx) => {
                // const icons = [AcademicCapIcon, SparklesIcon, PaintBrushIcon];
                // const icon = icons[idx] || AcademicCapIcon;
                return (
                  <AnimatedGridItem key={idx} delay={idx * 0.15}>
                    <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 hover:border-blue-200 hover:shadow-subtle transition-all duration-300">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 text-blue-500">
                          <icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900">
                            {item.name}
                          </h4>
                          <p className="text-xs text-slate-500">{item.role}</p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 italic leading-relaxed">
                        "{item.text}"
                      </p>
                    </div>
                  </AnimatedGridItem>
                );
              })}
            </div>
          </div>
        </section>
        </AnimatedSection>

        {/* Core Values */}
        <AnimatedSection>
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-blue-50/50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-3 flex items-center justify-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                  <CheckCircleIcon className="w-4 h-4" />
                </span>
                {t.valuesTitle}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <ValueItem
                icon={CurrencyEuroIcon}
                title={t.valueAffordabilityTitle}
                description={t.valueAffordabilityDesc}
                delay={0}
              />
              <ValueItem
                icon={ShieldCheckIcon}
                title={t.valueQualityTitle}
                description={t.valueQualityDesc}
                delay={0.1}
              />
              <ValueItem
                icon={LockClosedIcon}
                title={t.valueTrustTitle}
                description={t.valueTrustDesc}
                delay={0.2}
              />
              <ValueItem
                icon={SparklesIcon}
                title={t.valueInnovationTitle}
                description={t.valueInnovationDesc}
                delay={0.3}
              />
            </div>
          </div>
        </section>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection>
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-4">
              {t.ctaTitle}
            </h2>
            <p className="text-slate-600 text-base leading-relaxed mb-8">
              {t.ctaSubtitle}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all duration-200 hover:shadow-lg"
            >
              {t.ctaButton}
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </motion.div>
        </section>
        </AnimatedSection>
      </div>
      <Footer />
    </NavBar>
    </MainLayout>
  );
};

export default About;

